import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AtlasGqlTableViewsQuery,
  TableViewsDocument,
  useCreateTableViewMutation,
  useTableViewLazyQuery,
  useTableViewsQuery,
  useUpdateTableViewMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { getItem, setItem } from 'utils/storage';

import type { DashboardLayoutState } from '../types';

import { validateAndMigrate } from './validateAndMigrate';

/** Namespaced + versioned so it can never collide with a real data-table view id. */
const RESERVED_TABLE_NAME = 'dashboard:widget-layout:v1';
const RESERVED_VIEW_NAME = '__dashboard__';
const STORAGE_PREFIX = 'DASHBOARD_WIDGET_LAYOUT';
const SAVE_DEBOUNCE_MS = 750;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseDashboardPersistenceResult {
  /** Layout to render: server record if present, else localStorage, else seed. */
  loadedLayout: DashboardLayoutState;
  /** True once the initial server read has settled. */
  isLoaded: boolean;
  /** Persist a layout (immediate localStorage + debounced server write). */
  persist: (state: DashboardLayoutState) => void;
  saveStatus: SaveStatus;
  /** The customer everything above is scoped to; changes if the current customer does. */
  customerId: string;
}

/**
 * Per-user dashboard layout persistence, reusing the generated `TableView` GraphQL
 * trio + `customFields` JSON under a reserved tableName. localStorage is a
 * flash-free cache; the server record is the source of truth. No backend or
 * `.gql` changes — and no existing persistence code is modified.
 */
export function useDashboardPersistence(seed: DashboardLayoutState): UseDashboardPersistenceResult {
  const { customer } = useAccountContext();
  const customerId = customer?.id ?? 'anon';
  const storageKey = `${STORAGE_PREFIX}::${customerId}`;

  // A switch in another tab can change the customer under a mounted dashboard, and every
  // read below still describes the customer we mounted with — so treat those as unknown.
  const mountedCustomerIdRef = useRef(customerId);
  const isForeignCustomer = customerId !== mountedCustomerIdRef.current;
  // Read by flush(), which also runs from a debounce timer, `pagehide`, and unmount —
  // none of which wait for the effect below, so this has to be current at render time.
  const isForeignCustomerRef = useRef(isForeignCustomer);
  isForeignCustomerRef.current = isForeignCustomer;

  const {
    data: listData,
    loading: listLoading,
    error: listError,
  } = useTableViewsQuery({
    variables: { tableName: RESERVED_TABLE_NAME },
    fetchPolicy: 'cache-and-network',
  });
  const recordId: string | undefined = listData?.tableViews?.[0]?.id;

  const [fetchView, { data: viewData, error: viewError }] = useTableViewLazyQuery();
  useEffect(() => {
    if (recordId) {
      fetchView({ variables: { id: recordId } });
    }
  }, [recordId, fetchView]);

  const [createTableView] = useCreateTableViewMutation();
  const [updateTableView] = useUpdateTableViewMutation();

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // Lets the unmount-time flush still run its network write while skipping any
  // saveStatus update on an already-unmounted component.
  const mountedRef = useRef(true);

  // Synchronous localStorage seed for flash-free first paint.
  const localLayout = useMemo<DashboardLayoutState>(() => {
    const raw = getItem(storageKey, true);
    return validateAndMigrate(raw, seed);
  }, [storageKey, seed]);

  const serverCustomFields = viewData?.tableView?.customFields;
  const listSettled = listData !== undefined || Boolean(listError) || !listLoading;
  const viewSettled = viewData !== undefined || Boolean(viewError);
  // `isLoaded` means "the INITIAL read has settled"
  const hasLoadedRef = useRef(false);
  const isLoaded =
    !isForeignCustomer &&
    (hasLoadedRef.current || (listSettled && (recordId ? viewSettled : true)));
  useEffect(() => {
    if (isLoaded) {
      hasLoadedRef.current = true;
    }
  }, [isLoaded]);

  const loadedLayout = useMemo<DashboardLayoutState>(() => {
    if (isForeignCustomer) {
      return seed;
    }
    if (recordId && serverCustomFields !== undefined) {
      return validateAndMigrate(serverCustomFields, seed);
    }
    return localLayout;
  }, [isForeignCustomer, recordId, serverCustomFields, localLayout, seed]);

  // Refs that drive the single-in-flight save loop.
  const recordIdRef = useRef<string | undefined>(recordId);
  useEffect(() => {
    if (recordId) {
      recordIdRef.current = recordId;
    }
  }, [recordId]);

  // Tracks whether the TableViews list query has resolved at least once, so we
  // never CREATE a record before we know whether the server already has one.
  const listSettledRef = useRef(false);
  useEffect(() => {
    if (!listLoading) {
      listSettledRef.current = true;
    }
  }, [listLoading]);

  const latestRef = useRef<DashboardLayoutState | null>(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = useCallback(async () => {
    // Whatever is queued belongs to the previous customer; drop it rather than sending a
    // mutation the org-mismatch link would swallow without ever settling.
    if (isForeignCustomerRef.current) {
      latestRef.current = null;
      return;
    }
    if (inFlightRef.current) {
      return; // a save is running; it re-checks latestRef when it finishes
    }
    const state = latestRef.current;
    if (!state) {
      return;
    }
    // If we'd have to CREATE (no known record id) but the list query that tells us
    // whether a record already exists hasn't resolved yet, defer — creating now
    // could duplicate an existing per-user record. Re-arm and retry shortly.
    if (!recordIdRef.current && !listSettledRef.current) {
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
      return;
    }
    latestRef.current = null;
    inFlightRef.current = true;
    if (mountedRef.current) {
      setSaveStatus('saving');
    }

    try {
      const id = recordIdRef.current;
      if (id) {
        await updateTableView({ variables: { input: { id, customFields: state } } });
      } else {
        const result = await createTableView({
          variables: {
            input: {
              tableName: RESERVED_TABLE_NAME,
              name: RESERVED_VIEW_NAME,
              customFields: state,
            },
          },
          update: (cache, { data }) => {
            const created = data?.createTableView;
            if (!created) {
              return;
            }
            cache.updateQuery(
              { query: TableViewsDocument, variables: { tableName: RESERVED_TABLE_NAME } },
              (existing: AtlasGqlTableViewsQuery | undefined) =>
                existing
                  ? { ...existing, tableViews: [...(existing.tableViews ?? []), created] }
                  : existing
            );
          },
        });
        const createdId = result.data?.createTableView?.id;
        if (createdId) {
          recordIdRef.current = createdId;
        }
      }
      if (mountedRef.current) {
        setSaveStatus('saved');
      }
    } catch {
      if (mountedRef.current) {
        setSaveStatus('error');
      }
    } finally {
      inFlightRef.current = false;
      if (latestRef.current) {
        void flush(); // a newer state arrived mid-save
      }
    }
  }, [createTableView, updateTableView]);

  const persist = useCallback(
    (state: DashboardLayoutState) => {
      // This layout belongs to the previous customer, and storageKey is already the new
      // one's — writing it would hydrate them with someone else's dashboard after a reload.
      if (isForeignCustomer) {
        return;
      }
      setItem(storageKey, state, true); // immediate, flash-free cache
      latestRef.current = state;
      setSaveStatus('saving');
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [isForeignCustomer, storageKey, flush]
  );

  // Drop a debounced save caught by the switch: it is already in the previous customer's
  // localStorage, and Apollo's org-mismatch link blocks operations sent during a mismatch.
  useEffect(() => {
    if (!isForeignCustomer) {
      return;
    }
    clearTimeout(timerRef.current);
    latestRef.current = null;
    setSaveStatus('idle');
  }, [isForeignCustomer]);

  // Flush a pending (debounced) save on tab hide and on unmount, so a change made
  // within the debounce window isn't lost when the user navigates away.
  useEffect(() => {
    mountedRef.current = true;
    const flushPending = () => {
      if (latestRef.current) {
        void flush();
      }
    };
    window.addEventListener('pagehide', flushPending);
    return () => {
      // Mark unmounted first so the flush below still performs its network write but
      // skips its saveStatus updates (no update-on-unmounted-component warning).
      mountedRef.current = false;
      window.removeEventListener('pagehide', flushPending);
      clearTimeout(timerRef.current);
      flushPending();
    };
  }, [flush]);

  return { loadedLayout, isLoaded, persist, saveStatus, customerId };
}
